import React from "react";
import { Eye, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CustomerTags } from "./CustomerTags";
import { Customer } from "@/lib/types";
import EntityActions from "@/components/shared/EntityActions";
import CustomerBalanceDisplay from "./CustomerBalanceDisplay";
import { useCustomerBalances } from "@/hooks/useCustomerBalance";

interface CustomersTableProps {
  customers: Customer[];
  viewCustomerDetails: (customer: Customer) => void;
  setEditing: (customer: Customer | null) => void;
  setOpen: (open: boolean) => void;
  handleDeleteCustomer: (customerId: string) => void;
}

/**
 * CustomersTable - Table view for customers list
 * 
 * Displays customers in a tabular format with columns for:
 * - Name (clickable to view details)
 * - Type (Residential/Commercial)
 * - Contact info
 * - Balance (outstanding invoices with overdue indicator)
 * - Location
 * - Lead source
 * - Actions menu
 */
const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  viewCustomerDetails,
  setEditing,
  setOpen,
  handleDeleteCustomer
}) => {
  // Fetch balances for all customers in one query
  const customerIds = customers.map(c => c.id);
  const { data: balanceMap } = useCustomerBalances(customerIds);

  const getCustomerActions = (customer: Customer) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => viewCustomerDetails(customer),
    },
    {
      label: "Edit",
      icon: Edit,
      onClick: () => { setEditing(customer); setOpen(true); },
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: () => handleDeleteCustomer(customer.id),
      destructive: true,
      requiresConfirmation: {
        title: "Delete Customer?",
        description: "This action cannot be undone. This will permanently delete the customer and all associated data.",
      },
    },
  ];

  const getDisplayName = (customer: Customer) => {
    if (customer.customer_type === 'commercial' && customer.business_name) {
      return customer.business_name;
    }
    return `${customer.name} ${customer.last_name}`;
  };

  const getLocation = (customer: Customer) => {
    const parts = [customer.city, customer.province_state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="hidden md:table-cell">Type</TableHead>
          <TableHead className="hidden lg:table-cell">Phone</TableHead>
          <TableHead className="hidden md:table-cell">Email</TableHead>
          <TableHead className="hidden lg:table-cell">Balance</TableHead>
          <TableHead className="hidden xl:table-cell">Location</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {customers.map((customer) => {
          const balance = balanceMap?.get(customer.id);
          
          return (
            <TableRow 
              key={customer.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => viewCustomerDetails(customer)}
            >
              <TableCell>
                <div>
                  <span className="font-medium">{getDisplayName(customer)}</span>
                  {customer.tags && customer.tags.length > 0 && (
                    <div className="mt-1">
                      <CustomerTags tags={customer.tags.slice(0, 2)} />
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell capitalize">
                {customer.customer_type || 'residential'}
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                {customer.cell_numbers && customer.cell_numbers.length > 0 
                  ? customer.cell_numbers[0] 
                  : <span className="text-muted-foreground">—</span>
                }
              </TableCell>
              <TableCell className="hidden md:table-cell">
                {customer.emails && customer.emails.length > 0 
                  ? customer.emails[0] 
                  : <span className="text-muted-foreground">—</span>
                }
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <CustomerBalanceDisplay balance={balance} variant="compact" />
              </TableCell>
              <TableCell className="hidden xl:table-cell">
                {getLocation(customer) || <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => viewCustomerDetails(customer)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <EntityActions actions={getCustomerActions(customer)} size="sm" />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default CustomersTable;
